import { ArrowLeft, Shield, Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header Bar */}
      <div className="bg-slate-900 text-white py-4">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza a főoldalra
          </Link>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Shield className="w-4 h-4" />
            Adatvédelem
          </div>
        </div>
      </div>

      {/* Title Section */}
      <div className="bg-blue-900 py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Shield className="w-4 h-4" />
            ADATVÉDELEM
          </div>
          <h1
            className="text-3xl md:text-5xl font-extrabold tracking-tight text-white mb-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="privacy-title"
          >
            Adatvédelmi Tájékoztató és Nyilatkozat
          </h1>
          <p className="text-blue-200 text-base md:text-lg">
            TURPA-BUILD KFT. — Hatályos: 2025. 08. 04-től visszavonásig, vagy módosításig
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 md:px-12 max-w-4xl py-12 md:py-16" data-testid="privacy-content">
        <article className="prose prose-slate max-w-none" style={{ fontFamily: "'DM Sans', sans-serif" }}>

          {/* Table of Contents */}
          <nav className="bg-slate-50 rounded-2xl p-6 md:p-8 mb-12 border border-slate-100">
            <h2 className="text-lg md:text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Tartalom
            </h2>
            <ol className="space-y-2 text-slate-600 text-sm md:text-base list-decimal list-inside">
              <li><a href="#section-1" className="hover:text-blue-700 transition-colors">Adatkezelő adatai</a></li>
              <li><a href="#section-2" className="hover:text-blue-700 transition-colors">Adatvédelmi nyilatkozat</a></li>
              <li><a href="#section-3" className="hover:text-blue-700 transition-colors">Fogalmak</a></li>
              <li><a href="#section-4" className="hover:text-blue-700 transition-colors">Az adatkezelés alapvető elvei</a></li>
              <li><a href="#section-5" className="hover:text-blue-700 transition-colors">Kezelt adatok</a></li>
              <li><a href="#section-6" className="hover:text-blue-700 transition-colors">Az adatok biztonsága</a></li>
              <li><a href="#section-7" className="hover:text-blue-700 transition-colors">Az adatok továbbítása, átadása</a></li>
              <li><a href="#section-8" className="hover:text-blue-700 transition-colors">Az érintettek jogai</a></li>
            </ol>
          </nav>

          {/* Section 1 */}
          <section id="section-1" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              1. Adatkezelő adatai
            </h2>
            <div className="bg-slate-50 rounded-xl p-6 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm md:text-base">
                <div>
                  <span className="font-semibold text-slate-700">Megnevezés:</span>
                  <span className="text-slate-600 ml-2">TURPA-BUILD KFT.</span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Székhely:</span>
                  <span className="text-slate-600 ml-2">5231 Fegyvernek, Jelovich György út 8/a.</span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Adószám:</span>
                  <span className="text-slate-600 ml-2">26584094-2-16</span>
                </div>
                <div>
                  <span className="font-semibold text-slate-700">Cégjegyzékszám:</span>
                  <span className="text-slate-600 ml-2">16-09-018294</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="font-semibold text-slate-700">Telefon:</span>
                  <a href="tel:+36309669528" className="text-blue-700 hover:underline ml-1">0630 966 9528</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="font-semibold text-slate-700">Email:</span>
                  <a href="mailto:istvanturo06@gmail.com" className="text-blue-700 hover:underline ml-1">istvanturo06@gmail.com</a>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section id="section-2" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              2. Adatvédelmi nyilatkozat
            </h2>
            <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed">
              <p>
                Ez az Adatvédelmi tájékoztató és nyilatkozat (a továbbiakban: Tájékoztató) a TURPA-BUILD KFT. (továbbiakban: Adatkezelő) tevékenységére vonatkozóan tartalmazza az adatvédelemmel kapcsolatos információkat.
              </p>
              <p>
                Szolgáltatásunk igénybevétele során Ön megadja személyes adatait. Ezeket az adatokat a legnagyobb körültekintéssel, a jogszabályok előírásainak megfelelően kezeljük, és minden esetben igyekszünk az Ön adatkezeléssel kapcsolatos igényeinek és elvárásainak megfelelni. Az adatok kezelése során mindig különös gondossággal járunk el, és az adatokat védjük az illetéktelen hozzáféréstől. Ezt kiemelt fontosságúnak tekintjük.
              </p>
              <p className="font-semibold text-slate-700">Az adatkezelési tevékenységünket meghatározó jogszabályok elsősorban:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Az Európai Parlament és a Tanács (EU) 2016/679 rendelete (GDPR)</li>
                <li>Az információs önrendelkezési jogról és az információszabadságról szóló 2011. évi CXII. törvény (Infotv.)</li>
                <li>Magyarország Alaptörvénye</li>
                <li>A Polgári törvénykönyvről szóló 2013. évi V. törvény (Ptk.)</li>
                <li>2003. évi C. törvény az elektronikus hírközlésről (Eht.)</li>
                <li>Az elektronikus kereskedelmi szolgáltatások egyes kérdéseiről szóló 2001. évi CVIII. törvény (Eker tv.)</li>
                <li>Az adózás rendjéről szóló 2017. évi CL. törvény (Art.)</li>
                <li>A számvitelről szóló 2000. évi C. törvény (Sztv. tv.)</li>
                <li>A fogyasztóvédelemről szóló 1997. évi CLV. törvény (Fogyv tv.)</li>
                <li>A gazdasági reklámtevékenység alapvető feltételeiről szóló 2008. évi XLVIII. törvény (Grtv.)</li>
              </ul>
              <p>
                A Tájékoztató célja, hogy ismertesse a szolgáltatásunkat igénybe vevő személyek adatátadással, adatkezeléssel, adatvédelemmel kapcsolatos jogait, kötelezettségeit, az általunk kezelt adatok körét, az adatok kezelésének elvét és módszereit, célját, jogalapját és időtartamát.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="section-3" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              3. Fogalmak
            </h2>
            <div className="space-y-5 text-slate-600 text-sm md:text-base leading-relaxed">
              <div>
                <p className="font-semibold text-slate-800">GDPR:</p>
                <p>Az Európai Parlament és a Tanács (EU) 2016/679 rendelete (2016. április 27.) a természetes személyeknek a személyes adatok kezelése tekintetében történő védelméről és az ilyen adatok szabad áramlásáról, valamint a 95/46/EK irányelv hatályon kívül helyezéséről (általános adatvédelmi rendelet).</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Személyes adat:</p>
                <p>Az érintettre vonatkozó bármely információ, például valamely azonosító, név, szám, helymeghatározó adat, online azonosító vagy a természetes személy testi, fiziológiai, genetikai, szellemi, gazdasági, kulturális vagy szociális azonosságára vonatkozó adat.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Különleges adatok:</p>
                <p>A faji vagy etnikai származásra, politikai véleményre, vallási vagy világnézeti meggyőződésre vagy szakszervezeti tagságra utaló személyes adatok, valamint a természetes személyek egyedi azonosítását célzó genetikai és biometrikus adatok, az egészségügyi adatok és a természetes személyek szexuális életére vagy szexuális irányultságára vonatkozó személyes adatok.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Adatkezelés:</p>
                <p>Az alkalmazott eljárástól függetlenül a személyes adatokon vagy adatállományokon végzett bármely művelet vagy a műveletek összessége, így különösen gyűjtése, felvétele, rögzítése, rendszerezése, tagolása, tárolása, megváltoztatása, átalakítása, felhasználása, lekérdezése, továbbítása, nyilvánosságra hozatala, összehangolása vagy összekapcsolása, zárolása, törlése és megsemmisítése, az adatokba való betekintés, valamint az adatok további felhasználásának megakadályozása, fénykép-, hang- vagy képfelvétel készítése, valamint a személy azonosítására alkalmas fizikai jellemzők rögzítése.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Adatkezelő:</p>
                <p>Az a természetes vagy jogi személy, illetve jogi személyiséggel nem rendelkező szervezet, aki vagy amely önállóan vagy másokkal együtt a személyes adatok kezelésének célját és eszközeit meghatározza, az adatkezelésre vonatkozó döntéseket meghozza és végrehajtja, vagy az adatfeldolgozóval végrehajtatja.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Adatfeldolgozó:</p>
                <p>Az a természetes vagy jogi személy, illetve jogi személyiséggel nem rendelkező szervezet, aki, vagy amely az adatkezelő nevében személyes adatokat kezel.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Érintett:</p>
                <p>Bármely meghatározott személyes adat alapján azonosított vagy — közvetlenül vagy közvetve, egy vagy több tényező alapján — azonosítható természetes személy.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Adattovábbítás:</p>
                <p>A személyes adat meghatározott harmadik személy számára történő hozzáférhetővé tétele. Az EGT-tagállamokba, illetve az Európai Unió szervei felé irányuló adattovábbítást úgy kell tekinteni, mintha Magyarország területén belüli adattovábbításra kerülne sor.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Adattörlés/törlés:</p>
                <p>Az adatok felismerhetetlenné tétele tartalomtörléssel vagy ezzel egyenértékű eredményt lehetővé tevő módon.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Adatvédelmi incidens:</p>
                <p>A biztonság olyan sérülése, amely a továbbított, tárolt vagy más módon kezelt személyes adatok véletlen vagy jogellenes megsemmisítését, elvesztését, megváltoztatását, jogosulatlan közlését vagy az azokhoz való jogosulatlan hozzáférést eredményezi.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">EGT-tagállam:</p>
                <p>Az Európai Unió tagállama és az Európai Gazdasági Térségről szóló megállapodásban részes más állam, továbbá az az állam, amelynek állampolgára az Európai Unió és tagállamai, valamint az Európai Gazdasági Térségről szóló megállapodásban nem részes állam között létrejött nemzetközi szerződés alapján az Európai Gazdasági Térségről szóló megállapodásban részes állam állampolgárával azonos jogállást élvez.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Harmadik ország:</p>
                <p>Minden olyan állam, amely nem EGT-tagállam.</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800">NAIH:</p>
                <p>Nemzeti Adatvédelmi és Információszabadság Hatóság, Magyarország vonatkozásában a GDPR szerinti felügyeleti hatóság.</p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section id="section-4" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              4. Az adatkezelés alapvető elvei
            </h2>
            <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed">
              <p>A személyes adatok:</p>
              <ul className="list-disc list-inside space-y-3 pl-2">
                <li>kezelését jogszerűen és tisztességesen, valamint az érintett számára átlátható módon végezzük (<em>„jogszerűség, tisztességes eljárás és átláthatóság"</em>);</li>
                <li>gyűjtése csak meghatározott, egyértelmű és jogszerű célból történhet (<em>„célhoz kötöttség"</em>);</li>
                <li>az adatkezelés céljai szempontjából megfelelőek és relevánsak kell, hogy legyenek, és a szükségesre kell korlátozódniuk (<em>„adattakarékosság"</em>);</li>
                <li>kezelése során minden észszerű intézkedést megteszünk annak érdekében, hogy az adatok pontosak és naprakészek legyenek (<em>„pontosság"</em>);</li>
                <li>tárolását olyan formában végezzük, amely az érintettek azonosítását csak a szükséges ideig teszi lehetővé (<em>„korlátozott tárolhatóság"</em>);</li>
                <li>kezelését oly módon végezzük, hogy megfelelő technikai vagy szervezési intézkedések alkalmazásával biztosítva legyen a személyes adatok megfelelő biztonsága (<em>„integritás és bizalmas jelleg"</em>).</li>
              </ul>
              <p>Adatkezelőként felelősek vagyunk a fentieknek való megfelelésért, szükség esetén a megfelelést igazoljuk (<em>„elszámoltathatóság"</em>).</p>
            </div>
          </section>

          {/* Section 5 */}
          <section id="section-5" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              5. Kezelt adatok
            </h2>
            <h3 className="text-lg font-bold text-slate-800 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
              5.1 Szolgáltatás megrendelése és időpontfoglalás (kérdőív kitöltéssel)
            </h3>
            <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed">
              <p><span className="font-semibold text-slate-700">Érintettek:</span> Azok a természetes/jogi személyek, akik a szolgáltatást megrendelik és időpontot foglalnak.</p>
              <p><span className="font-semibold text-slate-700">Adatkezelés célja:</span> Kapcsolattartás, a megrendelés teljesítése.</p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse rounded-xl overflow-hidden text-sm">
                  <thead>
                    <tr className="bg-blue-900 text-white">
                      <th className="px-4 py-3 text-left font-semibold">Adatfajta</th>
                      <th className="px-4 py-3 text-left font-semibold">Jogalap</th>
                      <th className="px-4 py-3 text-left font-semibold">Őrzési idő</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["Név", "E-mail cím", "Telefonszám", "Lakcím", "Időpont"].map((item, i) => (
                      <tr key={item} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                        <td className="px-4 py-3 font-medium text-slate-700">{item}</td>
                        <td className="px-4 py-3 text-slate-600">{i === 0 ? "GDPR 6. cikk a) pont (hozzájárulás)" : ""}</td>
                        <td className="px-4 py-3 text-slate-600">{i === 0 ? "Hozzájárulás visszavonásáig" : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p>Amennyiben a szolgáltatás megrendelése és időpontfoglalás során megadja adatait egy kérdőív kitöltésével, az adatokat a megrendelés teljesítéséhez és kapcsolattartáshoz használjuk.</p>
              <p>A fenti adatok megadása nem kötelező, ezek hiányában azonban nem tudunk kapcsolatot tartani Önnel és nem tudjuk a megrendelését teljesíteni. A hozzájárulását bármikor indokolás nélkül visszavonhatja, ez azonban nem érinti a korábban, a hozzájárulása alapján történt adatkezeléseket.</p>
            </div>
          </section>

          {/* Section 6 */}
          <section id="section-6" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              6. Az adatok biztonsága
            </h2>
            <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed">
              <p>Technikai és szervezési intézkedésekkel, valamint eljárások kialakításával gondoskodunk az általunk kezelt személyes adatok biztonságáról.</p>
              <p>Az adatokat megfelelő intézkedésekkel védjük a jogosulatlan hozzáférés, megváltoztatás, továbbítás, nyilvánosságra hozatal, törlés vagy megsemmisítés, valamint a véletlen megsemmisülés és sérülés, továbbá az alkalmazott technika megváltozásából fakadó hozzáférhetetlenné válás ellen.</p>
              <p>A személyes adatokhoz csak azok a munkatársaink férnek hozzá, akiknek feladataik ellátásához szükségük van azok megismerésére.</p>
              <p className="font-semibold text-slate-700">Az adatok biztonsága érdekében:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>az informatikai rendszer tervezése és üzemeltetése során felmérjük és figyelembe vesszük a lehetséges kockázatokat, törekedve azok folyamatos csökkentésére</li>
                <li>figyelemmel kísérjük a felmerülő fenyegetéseket és sérülékenységeket, hogy időben intézkedhessünk azok elkerülése, elhárítása érdekében</li>
                <li>az informatikai eszközöket, valamint a papíron kezelt információkat védjük az illetéktelen fizikai hozzáférés, valamint a környezeti hatások ellen</li>
                <li>az informatikai rendszerünk megfigyelésével gondoskodunk a lehetséges problémák, események felderítéséről</li>
                <li>az üzemeltetésben résztvevő szolgáltatók kiválasztásánál alapvető szempont a megbízhatóság</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section id="section-7" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              7. Az adatok továbbítása, átadása
            </h2>
            <div className="space-y-4 text-slate-600 text-sm md:text-base leading-relaxed">
              <p>A szolgáltatásunkat igénybe vevő természetes személyek személyes adatait kizárólag az ebben a pontban meghatározott partnereinknek, adatfeldolgozóinknak továbbítjuk, illetve adjuk át, valamint a hatóságoknak megkeresés esetén.</p>
              <p>Az adatkezelési tevékenységünkben közreműködő partnerekkel, illetve adatfeldolgozókkal minden esetben az adatkezelés részleteire kiterjedő írásbeli megállapodást kötöttünk.</p>
              <p className="font-semibold text-slate-700">Az adatkezelésben közreműködő szerződéses partnereink:</p>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                <p className="font-semibold text-slate-800">HighLevel Inc. (tárhelyszolgáltató)</p>
                <p>400 North Saint Paul Street Suite 920 Dallas, TX 75201</p>
                <p className="mt-2">
                  Adatkezelési tájékoztató:{" "}
                  <a
                    href="https://storage.googleapis.com/msgsndr/knES3eSWYIsc5YSZ3YLI/media/6435747d41ff79e6e2117755.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:underline break-all"
                  >
                    Megtekintés
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section id="section-8" className="mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 pb-3 border-b-2 border-blue-600" style={{ fontFamily: "'Manrope', sans-serif" }}>
              8. Az érintettek jogai
            </h2>

            {/* 8.1 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.1 Előzetes tájékoztatáshoz való jog
              </h3>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>Az érintett jogosult arra, hogy a személyes adatokra vonatkozó adatkezelés megkezdése előtt átláthatóan, érthetően, világosan és könnyen hozzáférhetően írásban tájékoztatást kapjon az Adatkezelőtől. A tájékoztatást legkésőbb a személyes adatok megszerzésének időpontjában kell átadnia az Adatkezelőnek.</p>
                <p>Ha az adatkezelő a személyes adatokon a gyűjtésük céljától eltérő célból további adatkezelést kíván végezni, a további adatkezelést megelőzően tájékoztatnia kell az érintettet erről az eltérő célról és minden releváns kiegészítő információról.</p>
              </div>
            </div>

            {/* 8.2 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.2 A hozzáférés joga
              </h3>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>Az érintett jogosult arra, hogy az Adatkezelőtől visszajelzést kapjon arra vonatkozóan, hogy személyes adatainak kezelése folyamatban van-e, és ha ilyen adatkezelés folyamatban van, jogosult arra, hogy a személyes adatokhoz és a következő információkhoz hozzáférést kapjon:</p>
                <ol className="list-[lower-alpha] list-inside space-y-2 pl-2">
                  <li>az adatkezelés céljai;</li>
                  <li>az érintett személyes adatok kategóriái;</li>
                  <li>azon címzettek vagy címzettek kategóriái, akikkel a személyes adatokat közölték vagy közölni fogják;</li>
                  <li>a személyes adatok tárolásának tervezett időtartama;</li>
                  <li>az érintett joga a helyesbítés, törlés vagy az adatkezelés korlátozásának kéréséhez;</li>
                  <li>a felügyeleti hatósághoz címzett panasz benyújtásának joga;</li>
                  <li>ha az adatokat nem az érintettől gyűjtötték, a forrásukra vonatkozó információ;</li>
                  <li>az automatizált döntéshozatal ténye, ideértve a profilalkotást is.</li>
                </ol>
                <p>Az Adatkezelő az adatkezelés tárgyát képező személyes adatok másolatát az érintett rendelkezésére bocsátja.</p>
              </div>
            </div>

            {/* 8.3 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.3 A helyesbítéshez való jog
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Az érintett jogosult arra, hogy kérésére az Adatkezelő indokolatlan késedelem nélkül helyesbítse a rá vonatkozó pontatlan személyes adatokat. Figyelembe véve az adatkezelés célját, az érintett jogosult arra, hogy kérje a hiányos személyes adatok kiegészítését.
              </p>
            </div>

            {/* 8.4 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.4 A törléshez való jog („az elfeledtetéshez való jog")
              </h3>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>Az érintett jogosult arra, hogy kérésére az Adatkezelő indokolatlan késedelem nélkül törölje a rá vonatkozó személyes adatokat, ha az alábbi indokok valamelyike fennáll:</p>
                <ol className="list-[lower-alpha] list-inside space-y-2 pl-2">
                  <li>a személyes adatokra már nincs szükség abból a célból, amelyből azokat gyűjtötték;</li>
                  <li>az érintett visszavonja a hozzájárulását, és az adatkezelésnek nincs más jogalapja;</li>
                  <li>az érintett tiltakozik az adatkezelése ellen, és nincs elsőbbséget élvező jogszerű ok;</li>
                  <li>a személyes adatokat jogellenesen kezelték;</li>
                  <li>a személyes adatokat jogi kötelezettség teljesítéséhez törölni kell;</li>
                  <li>a személyes adatok gyűjtésére információs társadalommal összefüggő szolgáltatások kínálásával kapcsolatosan került sor.</li>
                </ol>
                <p>A törléshez való jog nem alkalmazandó, amennyiben az adatkezelés szükséges a véleménynyilvánítás szabadságához, jogi kötelezettség teljesítéséhez, népegészségügyi közérdekből, közérdekű archiválás céljából, vagy jogi igények előterjesztéséhez.</p>
              </div>
            </div>

            {/* 8.5 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.5 Az adatkezelés korlátozásához való jog
              </h3>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>Az érintett jogosult arra, hogy kérésére az Adatkezelő korlátozza az adatkezelést, ha az alábbiak valamelyike teljesül:</p>
                <ol className="list-[lower-alpha] list-inside space-y-2 pl-2">
                  <li>az érintett vitatja a személyes adatok pontosságát;</li>
                  <li>az adatkezelés jogellenes, és az érintett ellenzi az adatok törlését;</li>
                  <li>az Adatkezelőnek már nincs szüksége a személyes adatokra, de az érintett igényli azokat jogi igények érdekében;</li>
                  <li>az érintett tiltakozott az adatkezelés ellen.</li>
                </ol>
              </div>
            </div>

            {/* 8.6 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.6 Értesítéshez való jog
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Az érintettnek joga van ahhoz, hogy kérje az Adatkezelőtől azon címzettek megadását, akikkel személyes adatait közölték. Az Adatkezelő köteles a személyes adatok helyesbítéséről, törléséről vagy adatkezelés-korlátozásáról valamennyi olyan címzettet tájékoztatni, akivel a személyes adatot közölték, kivéve, ha ez lehetetlen, vagy aránytalanul nagy erőfeszítést igényel.
              </p>
            </div>

            {/* 8.7 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.7 Adathordozhatósághoz való jog
              </h3>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>Az érintett jogosult arra, hogy a rá vonatkozó személyes adatokat tagolt, széles körben használt, géppel olvasható formátumban megkapja, továbbá jogosult arra, hogy ezeket az adatokat egy másik adatkezelőnek továbbítsa, ha az adatkezelés hozzájáruláson vagy szerződésen alapul és automatizált módon történik.</p>
              </div>
            </div>

            {/* 8.8 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.8 A tiltakozáshoz való jog
              </h3>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>Az érintett jogosult arra, hogy a saját helyzetével kapcsolatos okokból bármikor tiltakozzon személyes adatainak kezelése ellen. Ebben az esetben az Adatkezelő a személyes adatokat nem kezelheti tovább, kivéve, ha bizonyítja, hogy az adatkezelést kényszerítő erejű jogos okok indokolják.</p>
                <p>Ha a személyes adatok kezelése közvetlen üzletszerzés érdekében történik, az érintett jogosult arra, hogy bármikor tiltakozzon a rá vonatkozó személyes adatok e célból történő kezelése ellen.</p>
              </div>
            </div>

            {/* 8.9 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.9 Automatizált döntéshozatal, profilalkotás
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Az érintett jogosult arra, hogy ne terjedjen ki rá az olyan, kizárólag automatizált adatkezelésen — ideértve a profilalkotást is — alapuló döntés hatálya, amely rá nézve joghatással járna vagy őt hasonlóképpen jelentős mértékben érintené.
              </p>
            </div>

            {/* 8.10 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.10 Tájékoztatáshoz való jog adatvédelmi incidensről
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Az érintettnek joga van ahhoz, hogy tájékoztatást kapjon az Adatkezelőnél előforduló, őt érintő adatvédelmi incidensről, amennyiben az valószínűsíthetően magas kockázattal jár a természetes személyek jogaira és szabadságaira nézve.
              </p>
            </div>

            {/* 8.11 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.11 Panasztétel a felügyeleti hatóságnál
              </h3>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>Minden érintett jogosult arra, hogy panaszt tegyen egy felügyeleti hatóságnál, ha megítélése szerint a rá vonatkozó személyes adatok kezelése megsérti a Rendeletet.</p>
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <p className="font-semibold text-slate-800">Magyar tagállami felügyeleti hatóság:</p>
                  <p className="mt-2">Nemzeti Adatvédelmi és Információszabadság Hatóság</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p><MapPin className="w-3.5 h-3.5 inline mr-1" />Postacím: 1363 Budapest, Pf. 9.</p>
                    <p><MapPin className="w-3.5 h-3.5 inline mr-1" />Székhely: 1055 Budapest, Falk Miksa utca 9-11.</p>
                    <p>Honlap: <a href="http://www.naih.hu" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">www.naih.hu</a></p>
                    <p><Phone className="w-3.5 h-3.5 inline mr-1" />Telefon: 06-1-391-1400</p>
                    <p><Mail className="w-3.5 h-3.5 inline mr-1" />E-mail: <a href="mailto:ugyfelszolgalat@naih.hu" className="text-blue-700 hover:underline">ugyfelszolgalat@naih.hu</a></p>
                  </div>
                </div>
              </div>
            </div>

            {/* 8.12 */}
            <div className="mb-8">
              <h3 className="text-base md:text-lg font-bold text-slate-800 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                8.12 Bírósági jogorvoslathoz való jog
              </h3>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed">
                Minden természetes és jogi személy jogosult a hatékony bírósági jogorvoslatra a felügyeleti hatóság rá vonatkozó, jogilag kötelező erejű döntésével szemben. Minden érintett jogosult a hatékony bírósági jogorvoslatra, ha az illetékes felügyeleti hatóság nem foglalkozik a panasszal, vagy három hónapon belül nem tájékoztatja az érintettet.
              </p>
            </div>
          </section>

          {/* Contact for rights */}
          <section className="mb-12">
            <div className="bg-blue-50 rounded-2xl p-6 md:p-8 border border-blue-100">
              <h3 className="text-lg font-bold text-blue-900 mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Elérhetőségek a joggyakorlás érdekében
              </h3>
              <div className="space-y-3 text-slate-700 text-sm md:text-base">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Postai levélben:</span>
                    <span className="ml-2">5231 Fegyvernek, Jelovich György út 8/a.</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">E-mailben:</span>
                    <a href="mailto:istvanturo06@gmail.com" className="text-blue-700 hover:underline ml-2">istvanturo06@gmail.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-semibold">Személyesen:</span>
                    <span className="ml-2">telefonszámon egyeztetett módon.</span>
                  </div>
                </div>
                <p className="text-slate-500 text-sm mt-4 italic">
                  Telefonon személyes adatokkal kapcsolatos információkat nem áll módunkban kiadni, mivel nem tudjuk azonosítani a hívó felet.
                </p>
              </div>
            </div>
          </section>

        </article>

        {/* Back to home */}
        <div className="text-center pt-8 border-t border-slate-200">
          <Link
            to="/"
            data-testid="privacy-back-link-bottom"
            className="inline-flex items-center gap-2 bg-blue-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza a főoldalra
          </Link>
        </div>
      </div>

      {/* Mini Footer */}
      <div className="bg-slate-900 text-slate-500 text-center py-6 text-sm">
        © {new Date().getFullYear()} Vályog-Mester / TURPA-BUILD KFT. Minden jog fenntartva.
      </div>
    </div>
  );
};

export default PrivacyPolicy;
